import {
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  MinusFillSquare,
} from "@/icons";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { TransitionProps } from "@mui/material/transitions";
import { TreeViewBaseItem } from "@mui/x-tree-view";
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import {
  unstable_useTreeItem2 as useTreeItem2,
  UseTreeItem2Parameters,
  UseTreeItem2Status,
} from "@mui/x-tree-view/useTreeItem2";
import clsx from "clsx";
import * as React from "react";
import { ITreeViewItemModel, SelectTriggerEnum } from ".";

const StyledTreeItemRoot = TreeItem2Root;

const CustomTreeItemContent = TreeItem2Content;

function TransitionComponent(props: TransitionProps) {
  return <Collapse {...props} />;
}

interface CustomLabelProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  expandable?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

function CustomLabel({
  icon: Icon,
  expandable,
  children,
  ...other
}: CustomLabelProps) {
  return (
    <TreeItem2Label
      {...other}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {Icon}
      <Box className="h6 h-full w-full">{children}</Box>
    </TreeItem2Label>
  );
}

const isExpandable = (reactChildren: React.ReactNode) => {
  if (Array.isArray(reactChildren)) {
    return reactChildren.length > 0 && reactChildren.some(isExpandable);
  }
  return Boolean(reactChildren);
};
const getIconFromStatus = (
  status: UseTreeItem2Status,
  isAllChildsSelected: boolean,
  eventClickIcon?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
) => {
  if (status.selected) {
    if (isAllChildsSelected) {
      return (
        <Box
          component={CheckBoxIcon}
          className="labelIcon h5  w-5 h-5 text-primary"
          onClick={eventClickIcon}
        />
      );
    } else {
      return (
        <Box
          component={MinusFillSquare}
          className="labelIcon h5  w-5 h-5"
          onClick={eventClickIcon}
        />
      );
    }
  } else if (status.disabled) {
    return (
      <Box
        component={CheckBoxOutlineBlankIcon}
        className="labelIcon h5  w-5 h-5 text-disabled"
        onClick={eventClickIcon}
      />
    );
  } else {
    return (
      <Box
        component={CheckBoxOutlineBlankIcon}
        className="labelIcon h5  w-5 h-5 text-black"
        onClick={eventClickIcon}
      />
    );
  }
};
export interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {
  selectItems: string[] | undefined;
  selectTrigger?: SelectTriggerEnum;
  checkboxSelection?: boolean;
  rightIconAction?: (
    data: TreeViewBaseItem<ITreeViewItemModel>
  ) => React.ReactNode;
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const {
    id,
    itemId,
    label,
    disabled,
    children,
    className,
    selectItems,
    selectTrigger,
    checkboxSelection,
    rightIconAction,
    ...other
  } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  const expandable = isExpandable(children);
  const item: TreeViewBaseItem<ITreeViewItemModel> = publicAPI.getItem(itemId);
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    publicAPI.selectItem({
      event,
      itemId,
      keepExistingSelection: true,
      shouldBeSelected: !status.selected,
    });
  };
  const checkAllChildsSelected = (
    itemCheck: TreeViewBaseItem<ITreeViewItemModel>
  ) => {
    let isChildsValidate =
      !itemCheck.children || itemCheck.children.length === 0;
    if (isChildsValidate) return isChildsValidate;
    if (itemCheck.children && itemCheck.children?.length > 0) {
      isChildsValidate = itemCheck.children.every((c) =>
        selectItems?.includes(c.id)
      );
      itemCheck.children.forEach((c) => {
        isChildsValidate = isChildsValidate && checkAllChildsSelected(c);
      });
    }
    return isChildsValidate;
  };
  const isAllChildsSelected = checkAllChildsSelected(item);
  let icon = getIconFromStatus(
    status,
    isAllChildsSelected,
    selectTrigger === SelectTriggerEnum.CheckBox ? handleClick : undefined
  );
  return (
    <TreeItem2Provider itemId={itemId}>
      <StyledTreeItemRoot
        {...getRootProps(other)}
        className={clsx("MuiTreeItem2-root", className)}
      >
        <CustomTreeItemContent
          {...getContentProps({
            className: clsx("content-root", {
              "Mui-expanded": status.expanded,
              "Mui-selected": status.selected,
              "Mui-focused": status.focused,
              "Mui-disabled": status.disabled,
            }),
          })}
        >
          <Box
            className="content"
            onClick={
              selectTrigger === SelectTriggerEnum.LabelAndCheckBox
                ? handleClick
                : undefined
            }
          >
            {checkboxSelection && (
              <TreeItem2Checkbox
                {...getCheckboxProps()}
                size="small"
                icon={icon}
                checkedIcon={icon}
              />
            )}
            <CustomLabel
              {...getLabelProps({
                icon: null,
                expandable: expandable && status.expanded,
              })}
            >
              <Box className="flex w-full relative h-full flex-row items-center justify-between">
                {label}
                {rightIconAction && (
                  <Box className="absolute right-1">
                    {rightIconAction?.(item)}
                  </Box>
                )}
              </Box>
            </CustomLabel>
          </Box>
          <TreeItem2IconContainer
            {...getIconContainerProps()}
            className={clsx("icon-container")}
          >
            <TreeItem2Icon status={status} />
          </TreeItem2IconContainer>
        </CustomTreeItemContent>
        {children && <TransitionComponent {...getGroupTransitionProps()} />}
      </StyledTreeItemRoot>
    </TreeItem2Provider>
  );
});
export default CustomTreeItem;
